import clsx from "clsx";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { FetchedNote } from "@/services/api/fetchers/notes";
import { FetchedFolder } from "@/services/api/fetchers/folders";
import { FocusableNoteCard } from "@/features/notes/components/Note/NoteCard";
import { FocusableFolderCard } from "@/features/folders/components/FolderCard";
import { storage } from "@/shared/utils/local-storage";
import { NoteAction } from "@/features/notes/types";
import { FolderAction } from "@/features/folders/types";
import { ViewMode } from "@/app/store/deviceSlice";

interface CombinedListProps {
  folders: FetchedFolder[];
  notes: FetchedNote[];
  folderActions: (folder: FetchedFolder) => FolderAction[];
  noteActions: (note: FetchedNote) => NoteAction[];
  className?: string;
  onFolderPress: (id: number, wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
  onNotePress: (id: number, wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
  selectedNotes?: Set<number>;
  selectedFolders?: Set<number>;
  isSelectionMode?: boolean;
  viewMode?: ViewMode;
}

export function FoldersAndNotesList({
  folders,
  notes,
  folderActions,
  noteActions,
  className,
  onFolderPress,
  onNotePress,
  selectedNotes,
  selectedFolders,
  isSelectionMode = false,
  viewMode = "grid",
}: CombinedListProps) {
  const [focusedIndex, setFocusedIndex] = useState(storage.getNotesNavigationState().focusedIndex);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize actions to prevent unnecessary re-renders
  const memoizedNoteActions = useCallback(noteActions, [noteActions]);
  const memoizedFolderActions = useCallback(folderActions, [folderActions]);

  // Sort notes to show pinned ones first, but keep folders at the top
  // Only sort when pinned status actually changes to improve performance
  const sortedNotes = useMemo(() => {
    const pinnedNotes = notes.filter((note) => note.pinned);
    const unpinnedNotes = notes.filter((note) => !note.pinned);
    return [...pinnedNotes, ...unpinnedNotes];
  }, [notes]);

  // Calculate grid dimensions
  const getGridDimensions = useCallback(() => {
    // In list mode, always use 1 column
    if (viewMode === "list") {
      return { cols: 1 };
    }
    // In grid mode, use responsive columns
    const cols = window.innerWidth >= 768 ? 4 : window.innerWidth >= 640 ? 3 : 2;
    return { cols };
  }, [viewMode]);

  const [gridDimensions, setGridDimensions] = useState(getGridDimensions);

  // Create virtual rows for combined items (folders first, then notes)
  const virtualRows = useMemo(() => {
    const { cols } = gridDimensions;
    const rows = [];

    // Add folder rows
    for (let i = 0; i < folders.length; i += cols) {
      rows.push({
        type: "folders" as const,
        items: folders.slice(i, i + cols),
      });
    }

    // Add note rows
    for (let i = 0; i < sortedNotes.length; i += cols) {
      rows.push({
        type: "notes" as const,
        items: sortedNotes.slice(i, i + cols),
      });
    }

    return rows;
  }, [folders, sortedNotes, gridDimensions]);

  // Set up virtualizer for rows
  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => {
      let element = containerRef.current?.parentElement;
      while (element) {
        const style = window.getComputedStyle(element);
        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          return element;
        }
        element = element.parentElement;
      }
      return document.documentElement;
    },
    estimateSize: (index) => {
      const row = virtualRows[index];

      // List view: compact single-column layout
      if (viewMode === "list") {
        // Folder rows in list view: min-h-11 (44px) + gap-3 (12px) = 56px
        // Note rows in list view: 2 lines of content (~60px) + gap-3 (12px) = 72px
        return row?.type === "folders" ? 56 : 72;
      }

      // Grid view: standard multi-column layout
      // Folder rows: min-h-11 (44px) + gap-3 (12px) for vertical spacing = 56px
      // Note rows: h-64 (256px) + gap-3 (12px) for vertical spacing = 268px
      return row?.type === "folders" ? 56 : 268;
    },
    overscan: 2,
  });

  // Update grid dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setGridDimensions(getGridDimensions());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getGridDimensions]);

  // Update grid dimensions when viewMode changes
  useEffect(() => {
    setGridDimensions(getGridDimensions());
  }, [viewMode, getGridDimensions]);

  return (
    <div ref={containerRef} className={clsx("relative", className)} tabIndex={-1}>
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = virtualRows[virtualRow.index];
          if (!row) return null;

          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="pb-3"
            >
              <div
                className={clsx("grid h-full gap-3", {
                  "grid-cols-1": viewMode === "list",
                  "grid-cols-2 sm:grid-cols-3 md:grid-cols-4": viewMode === "grid",
                })}
              >
                {row.type === "folders" &&
                  row.items.map((folder) => {
                    // Find the actual folder index in the folders array
                    const folderIndex = folders.findIndex((f) => f.id === folder.id);
                    return (
                      <FocusableFolderCard
                        key={`folder-${folder.id}`}
                        folder={folder}
                        actions={memoizedFolderActions}
                        index={folderIndex}
                        isFocused={folderIndex === focusedIndex}
                        isSelected={selectedFolders?.has(folder.id) ?? false}
                        isSelectionMode={isSelectionMode}
                        viewMode={viewMode}
                        onFocus={setFocusedIndex}
                        onPress={onFolderPress}
                      />
                    );
                  })}
                {row.type === "notes" &&
                  row.items.map((note) => {
                    // Find the actual note index in the sortedNotes array, offset by folders.length
                    const noteIndex = sortedNotes.findIndex((n) => n.id === note.id);
                    const itemIndex = folders.length + noteIndex;
                    return (
                      <FocusableNoteCard
                        key={`note-${note.id}`}
                        note={note}
                        actions={memoizedNoteActions}
                        index={itemIndex}
                        isFocused={itemIndex === focusedIndex}
                        viewMode={viewMode}
                        onFocus={setFocusedIndex}
                        onPress={onNotePress}
                        isSelected={selectedNotes?.has(note.id) ?? false}
                        isSelectionMode={isSelectionMode}
                      />
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
